import { AxiosInstance } from 'axios';
import { EventEmailData } from 'types/backend';

type EventName = typeof EVENT_NAMES[keyof typeof EVENT_NAMES];

export type DynamicEmailData = {
    currentUserName: string;
    currentUserEmailAddress: string;
    territorySalesRepName: string;
    territorySalesRepEmail: string;
    territorySalesLeaderName: string;
    territorySalesLeaderEmail: string;
};

type GetToCCArrayResponse = {
    toData: EventEmailData[];
    ccData: EventEmailData[];
    to: string[];
    cc: string[];
};

export interface IEmailAddressService {
    getToCCArrays(eventName: EventName, params: DynamicEmailData): Promise<GetToCCArrayResponse>;
}

export class EmailAddressService implements IEmailAddressService {
    constructor(private httpClient: AxiosInstance) {}

    private async getEventEmails(eventName: typeof EVENT_NAMES[keyof typeof EVENT_NAMES]): Promise<EventEmailData[]> {
        const response = await this.httpClient.post(`/api/emailaddress/`, { eventName });
        return (response?.data || []) as EventEmailData[];
    }

    private getEmailData(type: 'to' | 'cc', eventEmails: EventEmailData[] = []) {
        return eventEmails.filter((eventEmail) => eventEmail.type.toLowerCase() === type) || [];
    }

    async getToCCArrays(
        eventName: EventName,
        {
            currentUserName,
            currentUserEmailAddress,
            territorySalesRepName,
            territorySalesRepEmail,
            territorySalesLeaderName,
            territorySalesLeaderEmail,
        }: DynamicEmailData
    ) {
        const eventEmails = await this.getEventEmails(eventName);

        const currentUserEventEmailData: EventEmailData = {
            id: `currentUser`,
            eventEmailId: 0,
            eventName,
            type: 'cc',
            emailAddressId: 0,
            name: currentUserName,
            emailAddress: currentUserEmailAddress,
        };

        const territorySalesRepEventEmailData: EventEmailData = {
            id: `territorySalesRep`,
            eventEmailId: 0,
            eventName,
            type: 'cc',
            emailAddressId: 0,
            name: territorySalesRepName,
            emailAddress: territorySalesRepEmail,
        };

        const territorySalesLeaderEventEmailData: EventEmailData = {
            id: `territorySalesLeader`,
            eventEmailId: 0,
            eventName,
            type: 'cc',
            emailAddressId: 0,
            name: territorySalesLeaderName,
            emailAddress: territorySalesLeaderEmail,
        };

        const toData = Array.from(
            new Set(
                [...this.getEmailData('to', eventEmails)].filter(
                    (emailEventData) =>
                        emailEventData.emailAddress !== currentUserEmailAddress && !!emailEventData.emailAddress
                )
            )
        );

        const ccData = Array.from(
            new Set(
                [
                    ...this.getEmailData('cc', eventEmails),
                    territorySalesRepEventEmailData,
                    territorySalesLeaderEventEmailData,
                    currentUserEventEmailData,
                ].filter(
                    (emailEventData) =>
                        !!emailEventData &&
                        !!emailEventData.emailAddress &&
                        !toData.map((to) => to.emailAddress).includes(emailEventData.emailAddress)
                )
            )
        );

        return {
            toData,
            ccData,
            to: toData.map((emailEventData) => emailEventData.emailAddress),
            cc: ccData.map((emailEventData) => emailEventData.emailAddress),
        };
    }
}

export const EVENT_NAMES = {
    tierChangeRequested: 'Tier Change Requested',
    tierChangeApproved: 'Tier Change Approved',
    tierChangeRejected: 'Tier Change Rejected',
    deviationRequestedLevel1: 'Deviation Requested Level 1',
    deviationRequestedLevel2: 'Deviation Requested Level 2',
    deviationApproved: 'Deviation Approved',
    deviationRejected: 'Deviation Rejected',
    marketPriceRequestedLevel1: 'Market Price Requested Level 1',
    marketPriceRequestedLevel2: 'Market Price Requested Level 2',
    marketPriceApproved: 'Market Price Approved',
    marketPriceRejected: 'Market Price Rejected',
} as const;
